import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import {
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Employee, EmployeeVcard } from "../employees.interface";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
  GetCurrentUserAction,
  UpdateEmployeeAndInstallSignatureAction,
  UpdateCurrentUserAction,
  UPDATE_CURRENT_USER_SUCCESS,
  UPDATE_CURRENT_USER_FAILURE,
  UPDATE_EMPLOYEE_AND_INSTALL_SIGNATURE_SUCCESS,
  UPDATE_EMPLOYEE_AND_INSTALL_SIGNATURE_FAILURE,
} from "../employees.actions";
import { validateNonRequiredPhoneNumber } from "../../shared/validators/phone-number.validator";
import { getCurrentEmployeeSelector } from "../employees.reducer";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../../shared/toastr.service";
import { parseFooterTemplate } from "../../shared/utils/footers.util";
import {
  AdvancedSettings,
  EmployeeSetting,
} from "../../settings/settings.interface";
import { validateUrl } from "../../shared/validators/url.validator";
import { AuthService } from "src/app/auth/auth.service";
import { saveAs } from "file-saver";
import { combineLatest, Subject } from "rxjs";
import { takeUntil, filter } from "rxjs/operators";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { EmployeeVcardComponent } from "../../shared/employee-vcard/employee-vcard.component";
import { EmployeesService } from "../employees.service";
import { SettingsService } from "../../settings/settings.service";
import { AdvancedSettingsService } from "../../advanced-settings/advanced-settings.service";

@Component({
  selector: "app-current-employee-form",
  templateUrl: "./current-employee-form.component.html",
  styleUrls: ["./current-employee-form.component.scss"],
  standalone: false,
})
export class CurrentEmployeeFormComponent implements OnInit, OnDestroy {
  isLoading = true;
  isSyncing = false;
  saving = false;
  private isApplyingEmployeeState = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  genderOptions: Array<{ value: "male" | "female"; labelKey: string }> = [
    { value: "male", labelKey: "employee.genderMale" },
    { value: "female", labelKey: "employee.genderFemale" },
  ];

  form: UntypedFormGroup = new UntypedFormGroup({
    email: new UntypedFormControl({ value: "", disabled: true }),
    givenName: new UntypedFormControl({ value: "", disabled: true }),
    familyName: new UntypedFormControl({ value: "", disabled: true }),
    userAbout: new UntypedFormControl(""),
    gender: new UntypedFormControl(null),
    countryOfOrigin: new UntypedFormControl(""),
    dynamicVariables: new FormGroup({}),
    customLinks: new FormArray([]),
    vcard: new FormArray([]),
    handwrittenSignature: new FormArray([
      new FormGroup({
        label: new FormControl(""),
        input: new FormControl(""),
        url: new FormControl(""),
      }),
    ]),
    bannerDisabled: new UntypedFormControl(false),
    signatureNameInput: new UntypedFormControl(""),
    chosenSignatureTemplate: new UntypedFormControl(null),
  });
  employee: Employee;
  employeeSettings: EmployeeSetting[] = [];
  dynamicEmployeeSettings: EmployeeSetting[] = [];
  settings: AdvancedSettings;
  parsedSignature: string;
  previewType: "external" | "internal" = "external";
  branchId: string | null = null;
  branchVariables: Record<string, unknown> = {};
  vcardEntries: EmployeeVcard[] = [];
  vcardLoadVersion = 0;
  @ViewChild(EmployeeVcardComponent) employeeVcard?: EmployeeVcardComponent;
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private actions$: Actions,
    private translate: TranslateService,
    private toastr: ToastrService,
    private employeesService: EmployeesService,
    private settingsService: SettingsService,
    private advancedSettingsService: AdvancedSettingsService,
    public authService: AuthService,
  ) {}

  get namePronunciationEnabled(): boolean {
    return !!this.authService.appUser?.optionalInterfaces?.namePronunciation;
  }

  get showNameProfile(): boolean {
    return this.namePronunciationEnabled || this.authService.isSuperAdmin;
  }

  get nameProfileInactive(): boolean {
    return this.authService.isSuperAdmin && !this.namePronunciationEnabled;
  }

  ngOnInit(): void {
    this.store.dispatch(new GetCurrentUserAction());

    combineLatest([
      this.store
        .select(getCurrentEmployeeSelector)
        .pipe(filter((employee) => employee !== null)),
      this.settingsService.getEmployeeSettings(true),
      this.advancedSettingsService.get(true),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([employee, employeeSettings, advancedSettings]) => {
        this.applyEmployeeState(employee, employeeSettings, advancedSettings);
      });

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isApplyingEmployeeState) {
        return;
      }
      this.updateParsedSignature();
    });

    this.actions$
      .pipe(
        ofType(
          UPDATE_CURRENT_USER_SUCCESS,
          UPDATE_CURRENT_USER_FAILURE,
          UPDATE_EMPLOYEE_AND_INSTALL_SIGNATURE_SUCCESS,
          UPDATE_EMPLOYEE_AND_INSTALL_SIGNATURE_FAILURE,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.saving = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyEmployeeState(
    employee: Employee,
    employeeSettings: EmployeeSetting[],
    advancedSettings: AdvancedSettings,
  ): void {
    if (this.isApplyingEmployeeState) {
      return;
    }

    this.isApplyingEmployeeState = true;
    this.employee = employee;
    this.employeeSettings = employeeSettings;
    this.dynamicEmployeeSettings = this.employeeSettings.filter(
      (setting) => !setting.entityBound,
    );
    this.settings = advancedSettings;
    this.pendingRemoveAvatar = false;

    const hasEmployee = !!(employee.hasEmployee ?? employee.employeeId);
    this.authService.hasEmployee = hasEmployee;

    try {
      if (hasEmployee) {
        this.initializeDynamicFields();
        this.configureProfileFields(employee);
        this.fillForm(employee);
        this.setPreviewType(employee);
        this.updateParsedSignature();
        this.authService.changeAvatarUrl(employee.thumbnailPhotoUrl);
        this.form.markAsPristine();
        this.form.markAsUntouched();
      }
      this.isLoading = false;
    } finally {
      this.isApplyingEmployeeState = false;
    }
  }

  private initializeDynamicFields(): void {
    const dynamicGroup = this.dynamicVariables;
    Object.keys(dynamicGroup.controls).forEach((key) => {
      dynamicGroup.removeControl(key, { emitEvent: false });
    });

    this.dynamicEmployeeSettings.forEach((setting: EmployeeSetting) => {
      const validators = [];
      if (setting.mandatory) {
        validators.push((control: FormControl) => Validators.required(control));
      }
      if (["phoneNumber", "mobileNumber"].includes(setting.name)) {
        validators.push((control: FormControl) =>
          validateNonRequiredPhoneNumber(control),
        );
      }

      dynamicGroup.addControl(
        setting.name,
        new FormControl(
          {
            value: "",
            disabled: !setting.overridable,
          },
          validators,
        ),
        { emitEvent: false },
      );
    });
  }

  private configureProfileFields(employee: Employee): void {
    this.employeeSettings
      .filter((setting) => setting.entityBound && setting.name !== "email")
      .forEach((setting) => {
        const control = this.form.get(setting.name);
        if (!control) {
          return;
        }
        control.setValidators(
          setting.mandatory ? [(c: FormControl) => Validators.required(c)] : [],
        );
        const syncedNameLock =
          (setting.name === "givenName" || setting.name === "familyName") &&
          employee.dataSource !== "LOCAL";
        if ((setting.overridable ?? true) && !syncedNameLock) {
          control.enable({ emitEvent: false });
        } else {
          control.disable({ emitEvent: false });
        }
        control.updateValueAndValidity({ emitEvent: false });
      });
  }

  profileFieldLabel(name: string): string {
    const label = this.employeeSettings
      ?.find((row) => row.name === name)
      ?.label?.trim();
    if (label) {
      return label;
    }
    return this.translate.instant(
      name === "email" ? "employee.email" : `employee.${name}`,
    ) as string;
  }

  private setPreviewType(employee: Employee): void {
    if (employee.signatureInternal && !employee.signature) {
      this.previewType = "internal";
    } else if (employee.signature && !employee.signatureInternal) {
      this.previewType = "external";
    } else {
      this.previewType = "external";
    }
  }

  get customLinks() {
    return this.form.get("customLinks") as FormArray;
  }

  get dynamicVariables() {
    return this.form.get("dynamicVariables") as FormGroup;
  }

  addLink(url = "", text = "") {
    this.customLinks.push(
      new UntypedFormGroup({
        url: new UntypedFormControl(url, [
          (control) => Validators.required(control),
          (control) => validateUrl(control as UntypedFormControl),
        ]),
        text: new UntypedFormControl(text, (control) =>
          Validators.required(control),
        ),
      }),
      { emitEvent: false },
    );
  }

  removeLink(index: number) {
    this.customLinks.removeAt(index);
  }

  fillForm(employee: Employee) {
    const dynamicValues: Record<string, unknown> = {};
    if (this.dynamicEmployeeSettings.length > 0) {
      this.dynamicEmployeeSettings.forEach((setting) => {
        dynamicValues[setting.name] =
          employee.dynamicVariables?.[setting.name] || "";
      });
    } else {
      Object.assign(
        dynamicValues,
        Object.fromEntries(
          Object.entries(employee.dynamicVariables || {}).filter(
            ([key]) =>
              !this.employeeSettings.some(
                (setting) => setting.name === key && setting.entityBound,
              ),
          ),
        ),
      );
    }

    const country = String(employee.countryOfOrigin ?? "")
      .trim()
      .toUpperCase();

    this.form.patchValue(
      {
        email: employee.email || "",
        givenName: employee.givenName || "",
        familyName: employee.familyName || "",
        userAbout: employee.userAbout || "",
        gender:
          employee.gender === "male" || employee.gender === "female"
            ? employee.gender
            : null,
        countryOfOrigin: /^[A-Z]{2}$/.test(country) ? country : "",
        dynamicVariables: dynamicValues,
        bannerDisabled: employee.bannerDisabled || false,
      },
      { emitEvent: false },
    );

    this.branchId = employee.branchId || null;
    this.branchVariables = { ...(employee.branchVariables || {}) };

    const storedHandwritten = employee.handwrittenSignature?.[0];
    const handwrittenArray = this.form.get("handwrittenSignature") as FormArray;
    handwrittenArray.clear({ emitEvent: false });

    if (storedHandwritten) {
      this.form.patchValue(
        {
          signatureNameInput:
            storedHandwritten.input || employee.fullName || "",
        },
        { emitEvent: false },
      );

      handwrittenArray.push(
        new FormGroup({
          label: new FormControl(storedHandwritten.label || ""),
          input: new FormControl(storedHandwritten.input || ""),
          url: new FormControl(storedHandwritten.url || ""),
        }),
        { emitEvent: false },
      );
    } else {
      this.form.patchValue(
        { signatureNameInput: employee.fullName || "" },
        { emitEvent: false },
      );
      handwrittenArray.push(
        new FormGroup({
          label: new FormControl(""),
          input: new FormControl(""),
          url: new FormControl(""),
        }),
        { emitEvent: false },
      );
    }

    this.customLinks.clear({ emitEvent: false });
    (employee.customLinks || []).forEach((link) => {
      this.addLink(link.url, link.text);
    });

    this.vcardEntries = [...(employee.vcard || [])];
    this.vcardLoadVersion++;
  }

  private buildPayload(): Employee {
    const formValue = this.form.getRawValue() as Partial<Employee>;
    const country = String(formValue.countryOfOrigin ?? "")
      .trim()
      .toUpperCase();
    const filteredDynamic = Object.fromEntries(
      Object.entries(formValue.dynamicVariables || {}).filter(
        ([key]) =>
          !this.employeeSettings.some(
            (setting) => setting.name === key && setting.entityBound,
          ),
      ),
    );
    const dynamicVariables = Object.fromEntries(
      Object.entries(filteredDynamic).map(([k, v]) => [k, v || null]),
    );
    const payload = {
      ...this.employee,
      ...formValue,
      email: this.employee.email,
      givenName: formValue.givenName || null,
      familyName: formValue.familyName || null,
      userAbout: formValue.userAbout || null,
      gender:
        formValue.gender === "male" || formValue.gender === "female"
          ? formValue.gender
          : null,
      countryOfOrigin: /^[A-Z]{2}$/.test(country) ? country : null,
      vcard: this.employeeVcard?.getPayload() ?? [],
      badges: this.employee.badges ?? [],
      domainSignatures: this.employee.domainSignatures ?? [],
      customLinks: Array.isArray(formValue.customLinks)
        ? formValue.customLinks
        : (this.employee.customLinks ?? []),
      handwrittenSignature: Array.isArray(formValue.handwrittenSignature)
        ? formValue.handwrittenSignature
        : (this.employee.handwrittenSignature ?? []),
      branchId: this.branchId,
      branchVariables:
        Object.keys(this.branchVariables).length > 0
          ? this.branchVariables
          : undefined,
      dynamicVariables,
    } as Employee;
    if (this.pendingRemoveAvatar) {
      payload.thumbnailPhotoUrl = null;
      payload.localAvatar = false;
    }
    return payload;
  }

  getCurrentSignature(): string {
    if (this.employee?.signature && this.employee?.signatureInternal) {
      return this.previewType === "internal"
        ? this.employee.signatureInternal
        : this.employee.signature;
    } else if (this.employee?.signatureInternal) {
      return this.employee.signatureInternal;
    } else {
      return this.employee?.signature;
    }
  }

  updateParsedSignature(): void {
    if (this.employee) {
      this.parsedSignature = parseFooterTemplate(
        this.getCurrentSignature(),
        this.form.getRawValue() as Record<string, string>,
      );
    }
  }

  onBranchChange(event: {
    branchId: string | null;
    branchVariables: Record<string, unknown>;
  }): void {
    this.branchId = event.branchId;
    this.branchVariables = event.branchVariables;
    this.form.markAsDirty();
  }

  onSubmit() {
    if (this.saving) {
      return;
    }
    this.errorStateMatcher.submitAttempted = true;
    if (!(this.form.valid && (this.employeeVcard?.isValid() ?? true))) {
      this.toastr.warning(
        this.translate.instant("employee.validationErrors") as string,
      );
      return;
    }
    this.saving = true;
    this.store.dispatch(new UpdateCurrentUserAction(this.buildPayload()));
  }

  copySignatureToClipboard() {
    void navigator.clipboard.writeText(this.parsedSignature);
    this.toastr.success(
      this.translate.instant("employee.copySignatureSuccess") as string,
    );
  }

  copySignaturePlainToClipboard() {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = this.parsedSignature;

    const htmlContent = tempDiv.innerHTML;

    void navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob([tempDiv.textContent || ""], {
          type: "text/plain",
        }),
      }),
    ]);
    this.toastr.success(
      this.translate.instant("employee.copySignatureSuccess") as string,
    );
  }

  downloadSignatureOutlook() {
    const htmlContent = this.parsedSignature;
    const [localPart, domain] = this.employee.email.split("@");
    const sanitizedLocalPart = localPart.replace(/\./g, "_");
    const sanitizedDomain = domain.replace(/\./g, "_");
    const fileName = `${sanitizedLocalPart}_${sanitizedDomain}.htm`;
    const blob = new Blob([htmlContent], { type: "text/html" });
    saveAs(blob, fileName);
  }

  saveAndReinstall() {
    if (this.saving) {
      return;
    }
    this.errorStateMatcher.submitAttempted = true;
    if (!(this.form.valid && (this.employeeVcard?.isValid() ?? true))) {
      this.toastr.warning(
        this.translate.instant("employee.validationErrors") as string,
      );
      return;
    }
    this.saving = true;
    this.store.dispatch(
      new UpdateEmployeeAndInstallSignatureAction(this.buildPayload()),
    );
  }

  onAvatarChanged(): void {
    this.store.dispatch(new GetCurrentUserAction());
  }

  canForceSyncEmployee(dataSource?: Employee["dataSource"]): boolean {
    return !!dataSource && dataSource !== "LOCAL" && dataSource !== "SAMPLE";
  }

  getEmployeeDataSourceLabel(dataSource?: Employee["dataSource"]): string {
    switch (dataSource) {
      case "GOOGLE":
        return "employee.google";
      case "MICROSOFT":
        return "employee.microsoft";
      case "CALAMARI":
        return "employee.calamari";
      default:
        return "employee.local";
    }
  }

  forceSyncEmployee(): void {
    if (
      !this.employee?.email ||
      this.isSyncing ||
      !this.canForceSyncEmployee(this.employee.dataSource)
    ) {
      return;
    }

    this.isSyncing = true;
    this.employeesService.syncEmployee(this.employee.employeeId).subscribe({
      next: (result) => {
        this.isSyncing = false;
        if (!result.success) {
          this.toastr.error(
            this.translate.instant("employee.syncFailed") as string,
          );
          return;
        }

        this.toastr.success(
          this.translate.instant("employee.syncCompleted") as string,
        );
        this.employeesService.getCurrentUser().subscribe({
          next: (user) => {
            this.applySyncedEmployee(user);
          },
          error: () => {
            this.store.dispatch(new GetCurrentUserAction());
          },
        });
      },
      error: () => {
        this.isSyncing = false;
        this.toastr.error(
          this.translate.instant("employee.syncError") as string,
        );
      },
    });
  }

  private applySyncedEmployee(synced: Partial<Employee>): void {
    this.employee = { ...this.employee, ...synced };

    const dynamicValues: Record<string, unknown> = {};
    if (this.dynamicEmployeeSettings.length > 0) {
      this.dynamicEmployeeSettings.forEach((setting) => {
        dynamicValues[setting.name] =
          this.employee.dynamicVariables?.[setting.name] || "";
      });
    } else {
      Object.assign(
        dynamicValues,
        Object.fromEntries(
          Object.entries(this.employee.dynamicVariables || {}).filter(
            ([key]) =>
              !this.employeeSettings.some(
                (setting) => setting.name === key && setting.entityBound,
              ),
          ),
        ),
      );
    }

    const country = String(this.employee.countryOfOrigin ?? "")
      .trim()
      .toUpperCase();

    this.form.patchValue(
      {
        email: this.employee.email || "",
        givenName: this.employee.givenName || "",
        familyName: this.employee.familyName || "",
        userAbout: this.employee.userAbout || "",
        gender:
          this.employee.gender === "male" || this.employee.gender === "female"
            ? this.employee.gender
            : null,
        countryOfOrigin: /^[A-Z]{2}$/.test(country) ? country : "",
        dynamicVariables: dynamicValues,
      },
      { emitEvent: false },
    );
    this.configureProfileFields(this.employee);
    if (synced.thumbnailPhotoUrl !== undefined) {
      this.authService.changeAvatarUrl(synced.thumbnailPhotoUrl);
    }
  }

  pendingRemoveAvatar = false;

  removeAvatar(): void {
    if (!this.employee?.thumbnailPhotoUrl && !this.pendingRemoveAvatar) {
      return;
    }
    this.pendingRemoveAvatar = true;
    this.form.markAsDirty();
  }

  get displayThumbnailPhotoUrl(): string | null {
    if (this.pendingRemoveAvatar) {
      return null;
    }
    return this.employee?.thumbnailPhotoUrl ?? null;
  }
}
